// 디렉토리 경로: client/src/components/management/
// 파일명: ManagementTab.tsx
// 파일 코드의 역할/설명: 스크립트 관리 탭 메인 컴포넌트 ([작성|모음] 세그먼트 전환 및 기존 작성모드/신규 모음뷰 분기)

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback, useEffect } from 'react'
import { useScripts } from '@/hooks'
import { ScriptEditor } from './ScriptEditor'
import { ScriptList } from './ScriptList'
import { DualPanelView } from './DualPanelView'
import { CollectionView } from './CollectionView'
import type { Script, PanelIndex, ScriptSortOption } from '@/types'
import styles from './Management.module.css'

// ============================================================================
// 2. ManagementTab Component
// ============================================================================
export function ManagementTab() {
  // ========================================================================
  // 2-1. Hooks
  // ========================================================================
  const {
    scripts,
    editScript,
    removeScript,
    filter,
    setFilter,
    resetFilter,
    filteredScripts,
    categories,
    isLoading,
    error,
  } = useScripts()

  // [SC-01] 작성 / 모음 뷰 모드
  const [viewMode, setViewMode] = useState<'edit' | 'collection'>('edit')

  // [SC-02] 작성 목록은 '작업 중 거쳐가는 곳' — 완성(✅) 글은 기본 숨김 (제1원칙 투표 A안 4:0)
  const [showCompleted, setShowCompleted] = useState(false)
  const completedCount = filteredScripts.filter((s) => Boolean(s.completedAt)).length
  const visibleScripts = showCompleted
    ? filteredScripts
    : filteredScripts.filter((s) => !s.completedAt)

  // ========================================================================
  // 2-2. [DP-01] 듀얼 패널 상태 관리
  // ========================================================================
  const [panel1Script, setPanel1Script] = useState<Script | null>(null)
  const [panel2Script, setPanel2Script] = useState<Script | null>(null)
  const [isNewScriptMode, setIsNewScriptMode] = useState(false)
  const [editorScript, setEditorScript] = useState<Script | null>(null)  // 전체 에디터로 열린 스크립트

  // ========================================================================
  // 2-3. [DP-01] Firestore 동기화 - 삭제된 스크립트 자동 정리
  // ========================================================================
  useEffect(() => {
    if (panel1Script && !scripts.find((s) => s.id === panel1Script.id)) {
      // 패널 1 스크립트가 삭제됨 → 패널 2 승격
      setPanel1Script(panel2Script)
      setPanel2Script(null)
    }
    if (panel2Script && !scripts.find((s) => s.id === panel2Script.id)) {
      // 패널 2 스크립트가 삭제됨
      setPanel2Script(null)
    }
  }, [scripts, panel1Script, panel2Script])

  // ========================================================================
  // 2-3b. [DP-01] Firestore 동기화 - 패널/에디터 스크립트 실시간 업데이트
  // ========================================================================
  useEffect(() => {
    if (panel1Script) {
      const updated = scripts.find((s) => s.id === panel1Script.id)
      if (updated && updated !== panel1Script) {
        setPanel1Script(updated)
      }
    }
    if (panel2Script) {
      const updated = scripts.find((s) => s.id === panel2Script.id)
      if (updated && updated !== panel2Script) {
        setPanel2Script(updated)
      }
    }
    if (editorScript && editorScript.id) {
      const updated = scripts.find((s) => s.id === editorScript.id)
      if (updated && updated !== editorScript) {
        setEditorScript(updated)
      } else if (!updated) {
        setEditorScript(null)  // 삭제된 경우
      }
    }
  }, [scripts, panel1Script, panel2Script, editorScript])

  // ========================================================================
  // 2-4. 새 스크립트 생성 핸들러
  // ========================================================================
  const handleNewScript = useCallback(() => {
    setIsNewScriptMode(true)
    // 새 스크립트 모드 진입 시 패널 유지 (닫지 않음)
  }, [])

  // ========================================================================
  // 2-5. [DP-01] 스크립트 선택 핸들러 - 자동 배치 로직
  // ========================================================================
  const handleSelectScript = useCallback((script: Script) => {
    // 새 스크립트 모드 종료
    setIsNewScriptMode(false)

    // 1. 이미 패널 1에 있는 스크립트 클릭 → 토글 해제
    if (panel1Script?.id === script.id) {
      // 패널 2 → 패널 1 승격
      setPanel1Script(panel2Script)
      setPanel2Script(null)
      return
    }

    // 2. 이미 패널 2에 있는 스크립트 클릭 → 토글 해제
    if (panel2Script?.id === script.id) {
      setPanel2Script(null)
      return
    }

    // 3. 패널 1이 비어있으면 → 패널 1에 배치
    if (!panel1Script) {
      setPanel1Script(script)
      return
    }

    // 4. 패널 2가 비어있으면 → 패널 2에 배치
    if (!panel2Script) {
      setPanel2Script(script)
      return
    }

    // 5. 양쪽 모두 차있으면 → 패널 1 교체
    setPanel1Script(script)
  }, [panel1Script, panel2Script])

  // ========================================================================
  // 2-6. [DP-01] 패널 닫기 핸들러
  // ========================================================================
  const handleClosePanel = useCallback((panelIndex: PanelIndex) => {
    if (panelIndex === 0) {
      // 패널 1 닫기 → 패널 2를 패널 1로 승격
      setPanel1Script(panel2Script)
      setPanel2Script(null)
    } else {
      // 패널 2 닫기
      setPanel2Script(null)
    }
  }, [panel2Script])

  // ========================================================================
  // 2-7. 에디터에서 열기 핸들러 (기존 스크립트를 전체 에디터로)
  // ========================================================================
  const handleOpenInEditor = useCallback((script: Script) => {
    setEditorScript(script)
    setIsNewScriptMode(false)
  }, [])

  // ========================================================================
  // 2-7b. 에디터 닫기 핸들러
  // ========================================================================
  const handleCloseEditor = useCallback(() => {
    setIsNewScriptMode(false)
    setEditorScript(null)
  }, [])

  // ========================================================================
  // 2-8. 스크립트 삭제 핸들러
  // ========================================================================
  const handleDeleteScript = useCallback(async (id: string) => {
    if (!window.confirm('이 스크립트를 삭제하시겠습니까?')) {
      return
    }

    const success = await removeScript(id)
    if (success) {
      // useEffect의 Firestore 동기화에서 자동으로 패널 정리됨
      console.log('[ManagementTab] 스크립트 삭제 완료:', id)
    } else {
      alert('스크립트 삭제에 실패했습니다.')
    }
  }, [removeScript])

  // ========================================================================
  // 2-9. 오른쪽 영역 렌더링 결정
  // ========================================================================
  const renderEditorSection = () => {
    // 1. 새 스크립트 모드
    if (isNewScriptMode) {
      return (
        <ScriptEditor
          key="new"
          script={{
            id: '',
            name: '',
            content: '',
            variables: [],
            createdAt: new Date(),
            updatedAt: new Date(),
          }}
          onClose={handleCloseEditor}
          onDelete={handleDeleteScript}
        />
      )
    }

    // 2. 기존 스크립트를 전체 에디터에서 열기
    if (editorScript) {
      return (
        <ScriptEditor
          key={editorScript.id || 'editor'}
          script={editorScript}
          onClose={handleCloseEditor}
          onDelete={handleDeleteScript}
        />
      )
    }

    // 3. 패널이 하나라도 있으면 DualPanelView
    if (panel1Script) {
      return (
        <DualPanelView
          panel1Script={panel1Script}
          panel2Script={panel2Script}
          onClosePanel={handleClosePanel}
          onDeleteScript={handleDeleteScript}
          onOpenInEditor={handleOpenInEditor}
        />
      )
    }

    // 3. 아무것도 없으면 placeholder
    return (
      <div className={styles.placeholder}>
        <p>스크립트를 선택하거나 새로 만들어주세요</p>
        <p className={styles.placeholderHint}>
          카드를 클릭하면 고정됩니다. 최대 2개까지 동시에 볼 수 있습니다.
        </p>
      </div>
    )
  }

  // ========================================================================
  // 2-10. Render
  // ========================================================================
  return (
    <div className={styles.managementTabWrapper}>
      {/* ================================================================
          상단 모드 세그먼트 컨트롤 [작성 | 모음]
          ================================================================ */}
      <div className={styles.tabModeHeader}>
        <div className={styles.segmentedControl} role="tablist" aria-label="스크립트 보기 모드">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'edit'}
            className={`${styles.segmentBtn} ${viewMode === 'edit' ? styles.active : ''}`}
            onClick={() => setViewMode('edit')}
          >
            ✏️ 작성
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'collection'}
            className={`${styles.segmentBtn} ${viewMode === 'collection' ? styles.active : ''}`}
            onClick={() => setViewMode('collection')}
          >
            📚 모음
          </button>
        </div>
      </div>

      {/* ================================================================
          작성 모드 서브트리 (모음 전환 시에도 unmount하지 않고 CSS 은닉하여 draft state 보존)
          ================================================================ */}
      <div
        className={`${styles.managementTab} ${viewMode !== 'edit' ? styles.hidden : ''}`}
      >
        {/* ================================================================
            스크립트 목록 영역
            ================================================================ */}
        <div className={styles.listSection}>
          {/* 목록 헤더 */}
          <div className={styles.listHeader}>
            <h3>스크립트 목록</h3>
            <button
              className={styles.newBtn}
              onClick={handleNewScript}
            >
              + 새 스크립트
            </button>
          </div>

          {/* [DP-01] 듀얼 패널 힌트 */}
          <div className={styles.dualPanelHint}>
            💡 카드를 클릭하여 고정 (최대 2개) | 다시 클릭하면 해제
          </div>

          {/* [SC-02] 완성 글 표시 토글 — 완성(✅)은 모음이 집, 작성 목록은 기본 미완성만 */}
          {completedCount > 0 && (
            <label className={styles.showCompletedToggle}>
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
              />
              <span>✅ 완성 포함 ({completedCount})</span>
            </label>
          )}

          {/* 필터 바 */}
          <div className={styles.filterBar}>
            <input
              type="text"
              value={filter.search}
              onChange={(e) => setFilter({ search: e.target.value })}
              placeholder="검색..."
              className={styles.searchInput}
            />
            <select
              value={filter.category}
              onChange={(e) => setFilter({ category: e.target.value })}
              className={styles.filterSelect}
            >
              <option value="">전체 카테고리</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <select
              value={filter.sortBy}
              onChange={(e) =>
                setFilter({ sortBy: e.target.value as ScriptSortOption })
              }
              className={styles.filterSelect}
            >
              <option value="newest">최신순</option>
              <option value="oldest">오래된순</option>
              <option value="name">이름순</option>
              <option value="category">카테고리순</option>
            </select>
            {(filter.search || filter.category) && (
              <button onClick={resetFilter} className={styles.resetBtn}>
                초기화
              </button>
            )}
          </div>

          {/* [SC-02] 전부 완성 상태 — 거짓 '없음' 안내 방지 (Codex P2) */}
          {!showCompleted && visibleScripts.length === 0 && completedCount > 0 ? (
            <div className={styles.emptyCompletedNotice}>
              <p>✅ 조건에 맞는 글이 모두 완성 처리되어 모음에 있습니다.</p>
              <p>위의 '완성 포함' 토글을 켜면 여기서도 볼 수 있어요.</p>
            </div>
          ) : (
          <ScriptList
            scripts={visibleScripts}
            onSelect={handleSelectScript}
            panel1Id={panel1Script?.id}
            panel2Id={panel2Script?.id}
            isLoading={isLoading}
            error={error}
          />
          )}
        </div>

        {/* ================================================================
            스크립트 상세/에디터 영역
            ================================================================ */}
        <div className={styles.editorSection}>
          {renderEditorSection()}
        </div>
      </div>

      {/* ================================================================
          모음 뷰 서브트리 (작성 모드일 때는 CSS 은닉)
          ================================================================ */}
      <div
        className={`${styles.collectionSection} ${viewMode !== 'collection' ? styles.hidden : ''}`}
      >
        <CollectionView
          scripts={scripts}
          editScript={editScript}
          onOpenInEditor={(script) => {
            setViewMode('edit')
            handleOpenInEditor(script)
          }}
        />
      </div>
    </div>
  )
}

export default ManagementTab

